import { memo } from "react";
import { useState } from "react";
import DrillModal from "../../shared/DrillModal";
import RecordModal from "../../shared/RecordModal";
import DashboardTable from "../../shared/DashboardTable";
import StatCardRow from "../../shared/StatCardRow";
import InfoBanner from "../../shared/InfoBanner";
import TableLoading from "../../shared/TableLoading";
import { fetchReportItems } from "../../services/dashboardApi";
import { toPlainText } from "../../utils/plainText";
import { reportItemDrillColumns } from "../../utils/drillHelpers";
import { useMountFlash } from "../../hooks/useMountFlash";
import "../../reportDashboard.scss";

const ASSET_TYPES = ["Project", "Vehicle", "Equipment", "Custom Asset"];

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Corrective-rate band shared by the day-of-week meters and the monthly trend labels.
const rateTone = (rate) =>
  rate > 12 ? "danger" : rate > 8 ? "warning" : "kudos";

// `type: "number"` keeps numeric sorting but right-aligns by default; the count
// columns read better centred under their headings.
const centeredCount = {
  type: "number",
  align: "center",
  headerAlign: "center",
};
const centered = { align: "center", headerAlign: "center" };

const formTypeColumns = [
  { field: "formTitle", headerName: "Form / Inspection Title", flex: 1 },
  { field: "kudos", headerName: "Kudos", width: 90, ...centeredCount },
  {
    field: "corrective",
    headerName: "Corrective",
    width: 100,
    ...centeredCount,
  },
  { field: "action", headerName: "Action", width: 90, ...centeredCount },
  {
    field: "reprimand",
    headerName: "Reprimand",
    width: 100,
    ...centeredCount,
  },
  { field: "total", headerName: "Total", width: 90, ...centeredCount },
];

const hotspotColumns = [
  {
    field: "assetType",
    headerName: "Asset Type",
    width: 110,
    cellClassName: "rdGridCell--text",
  },
  // minWidth per IncidentsSection's reporterColumns pattern — a flex column
  // with no floor gets crushed once rdGrid--2 collapses on a narrower viewport.
  { field: "category", headerName: "Asset", flex: 1, minWidth: 110 },
  { field: "count", headerName: "Corrective", width: 110, type: "number" },
];

const InspectionsSection = ({ data, apiCtx, refetching }) => {
  const formTypes = data.formTypes || [];
  const topCategories = data.topCategories || [];
  const topSubCategories = data.topSubCategories || [];
  const hotspots = data.hotspots || [];
  const trend = data.trend || [];
  const inspectorStats = data.inspectorStats || [];
  const dowHeatmap = data.dowHeatmap || { all: {}, corrective: {}, kudos: {} };
  const recurringQuestions = data.recurringQuestions || [];
  const assetBreakdown = data.assetBreakdown || [];
  const draftCounts = data.draftCounts || { inspections: 0 };
  const [drill, setDrill] = useState(null);
  const [detail, setDetail] = useState(null);

  // Page-payload-fed tables below have no fetch of their own to report; this
  // gives them a brief loading state when the section mounts (tab switch) and
  // the real one whenever the page payload is refetching.
  const mountFlash = useMountFlash();
  const tableLoading = refetching || mountFlash;

  // DrillModal now does its own server-paged fetching (see the component's own
  // header comment for why) — this just records which filter was clicked so the
  // modal's fetchPage closure can pull the matching rows a page at a time. There's
  // no longer a network round-trip here, so openDrill is synchronous.
  const openDrill = (title, filter) => {
    setDrill({ title, filter });
  };
  const closeDrill = () => setDrill(null);

  const drillByFormTitle = (formTitle) =>
    openDrill(`Form: ${formTitle}`, { formTitle });
  const drillByCategory = (category) =>
    openDrill(`Category: ${category}`, { itemType: "Corrective", category });
  const drillBySubCategory = (subCategory) =>
    openDrill(`Sub-Category: ${subCategory}`, {
      itemType: "Corrective",
      subCategory,
    });
  const drillByProject = (projectName) =>
    openDrill(`Project: ${projectName}`, {
      itemType: "Corrective",
      projectName,
    });
  const drillByAssetType = (assetType) =>
    openDrill(`Asset Type: ${assetType}`, { assetType });

  // Fleet — err, overall — average across all inspectors, all asset types
  // combined. Outlier threshold is 2x this rate. Surfaced in the UI as
  // "Inspector Fleet Average", never "Corrective Rate" -- this is a different
  // population than BenchmarksSection's Corrective Rate (which is scoped to
  // kpis.typedItemsTotal, not to inspector rows), and labelling both
  // "Corrective Rate" made them look like the same number when they aren't.
  const totalItems = inspectorStats.reduce((s, i) => s + i.totalInspections, 0);
  const totalCorrective = inspectorStats.reduce(
    (s, i) => s + i.correctiveItems,
    0,
  );
  const overallRate =
    totalItems > 0 ? Math.round((totalCorrective / totalItems) * 1000) / 10 : 0;

  const openInspectorDetail = (i) => {
    const isOutlier = i.correctiveRate > overallRate * 2;
    setDetail({
      kind: "inspector",
      payload: { ...i, isOutlier },
    });
  };

  const sortedInspectors = [...inspectorStats]
    .sort((a, b) => b.correctiveRate - a.correctiveRate)
    .slice(0, 20);
  const outliers = inspectorStats.filter(
    (i) => i.correctiveRate > overallRate * 2,
  );
  // "Kudos-Only Inspectors" stat card — zero corrective items, not literally
  // kudos === total (a row can still carry Action/Reprimand items and count
  // here); the row-level "★ Kudos only" marker below uses the stricter test.
  const kudosOnlyCount = inspectorStats.filter(
    (i) => i.totalInspections > 10 && i.correctiveItems === 0,
  ).length;

  const inspectorColumns = [
    {
      field: "inspector",
      headerName: "Inspector",
      flex: 1,
      cellClassName: "rdGridCell--text",
      renderCell: (params) => {
        const isOut = params.row.correctiveRate > overallRate * 2;
        const isKudosOnly =
          params.row.totalInspections > 10 &&
          params.row.kudos === params.row.totalInspections;
        return (
          <span>
            {params.value}
            {isOut && <span className="rdFlag">&#9888; OUTLIER</span>}
            {!isOut && isKudosOnly && (
              <span className="rdFlag is-kudos">&#9733; Kudos only</span>
            )}
          </span>
        );
      },
    },
    {
      field: "kudos",
      headerName: "Kudos",
      width: 90,
      ...centeredCount,
      cellClassName: "rdGridCell--text",
      renderCell: (params) => (
        <span className="rdValue is-kudos">{params.value}</span>
      ),
    },
    {
      field: "correctiveItems",
      headerName: "Corrective",
      width: 100,
      ...centeredCount,
      cellClassName: "rdGridCell--text",
      renderCell: (params) => (
        <span className="rdValue is-corrective">{params.value}</span>
      ),
    },
    { field: "action", headerName: "Action", width: 90, ...centeredCount },
    {
      field: "totalInspections",
      headerName: "Total",
      width: 90,
      ...centeredCount,
    },
    {
      field: "kudosRate",
      headerName: "Kudos%",
      width: 90,
      ...centeredCount,
      cellClassName: "rdGridCell--text",
      renderCell: (params) => (
        <span className="rdValue is-kudos">{params.value}%</span>
      ),
    },
    {
      field: "correctiveRate",
      headerName: "Corrective Rate",
      width: 150,
      ...centered,
      cellClassName: "rdGridCell--text",
      renderCell: (params) => {
        const cr = params.value;
        const isOut = cr > overallRate * 2;
        const tone = isOut ? "danger" : cr > overallRate ? "warning" : "muted";
        const barPct = Math.min(100, (cr / Math.max(overallRate * 3, 1)) * 100);
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span className="rdBar rdBar--thin" style={{ width: 60 }}>
              <span
                className={`rdBar__fill is-${isOut ? "danger" : cr > overallRate ? "warning" : "accent"}`}
                style={{ "--rd-fill": `${barPct}%` }}
              />
            </span>
            <span className={`rdValue is-${tone}`}>{cr}%</span>
          </span>
        );
      },
    },
    {
      field: "projects",
      headerName: "Assets",
      width: 90,
      ...centeredCount,
      cellClassName: "rdGridCell--text",
      renderCell: (params) => (
        <span className="rdValue is-muted">{params.value}</span>
      ),
    },
  ];

  const maxCat = Math.max(...topCategories.map((c) => c.count), 1);
  const categoryColumns = [
    // minWidth per IncidentsSection's reporterColumns pattern (see comment
    // there) — otherwise MUI's flex shrink has no floor once rdGrid--2 collapses.
    { field: "category", headerName: "Category", flex: 1, minWidth: 110 },
    {
      field: "distribution",
      headerName: "Distribution",
      flex: 1,
      minWidth: 110,
      sortable: false,
      valueGetter: (params) => params.row.count,
      renderCell: (params) => (
        <div className="rdBar rdBar--thin">
          <div
            className="rdBar__fill is-accent"
            style={{ "--rd-fill": `${(params.value / maxCat) * 100}%` }}
          />
        </div>
      ),
    },
    { field: "count", headerName: "Count", width: 90, type: "number" },
  ];

  const maxSubCat = Math.max(...topSubCategories.map((c) => c.count), 1);
  const subCategoryColumns = [
    { field: "subCategory", headerName: "Sub-Category", flex: 1, minWidth: 110 },
    {
      field: "distribution",
      headerName: "Distribution",
      flex: 1,
      minWidth: 110,
      sortable: false,
      valueGetter: (params) => params.row.count,
      renderCell: (params) => (
        <div className="rdBar rdBar--thin">
          <div
            className="rdBar__fill is-corrective"
            style={{ "--rd-fill": `${(params.value / maxSubCat) * 100}%` }}
          />
        </div>
      ),
    },
    { field: "count", headerName: "Count", width: 90, type: "number" },
  ];

  const maxDow = Math.max(...DAYS.map((d) => dowHeatmap.all[d] || 0), 1);
  const recentMonths = trend.slice(-8);
  const maxMonth = Math.max(...recentMonths.map((m) => m.total || 0), 1);

  const assetCards = ASSET_TYPES.map((type) => {
    const row = assetBreakdown.find((a) => a.assetType === type) || {
      assetType: type,
      total: 0,
      corrective: 0,
      action: 0,
      reprimand: 0,
      corrRate: 0,
    };
    return {
      label: row.assetType,
      value: row.total,
      tone: "accent",
      subtitle:
        row.total > 0
          ? `${row.corrective} corrective · ${row.action || 0} action · ${row.reprimand || 0} reprimand · ${row.corrRate}% rate`
          : "No inspection activity in range",
      onClick: () => drillByAssetType(row.assetType),
    };
  });

  return (
    <div>
      {draftCounts.inspections > 0 && (
        <InfoBanner>
          <strong>{draftCounts.inspections}</strong> inspection report
          {draftCounts.inspections === 1 ? "" : "s"}{" "}
          {draftCounts.inspections === 1 ? "is" : "are"} currently in Draft
          status and{" "}
          {draftCounts.inspections === 1 ? "has" : "have"} not been submitted
          — excluded from the totals below.
        </InfoBanner>
      )}

      <div className="rdSectionLabel rdSectionLabel--first">
        Inspections by Form Type
      </div>
      <div className="rdCard">
        <div className="rdTableWrap">
          <div className="rdCard__title rdCard__title--bar">
            <span className="rdCard__titleBar">Form Type Distribution</span>
            {formTypes.length > 0 && (
              <span className="rdCard__note">Click row to drill down</span>
            )}
          </div>
          <DashboardTable
            columns={formTypeColumns}
            rows={formTypes}
            getRowId={(r) => r.formTitle}
            onRowClick={(params) => drillByFormTitle(params.row.formTitle)}
            loading={tableLoading}
          />
        </div>
      </div>

      <div className="rdSectionLabel">Corrective Categories</div>
      <div className="rdCard">
        <div className="rdCard__hint">
          Broad Category level — see Corrective Sub-Categories below for the
          finer-grained breakdown
        </div>
        <div className="rdTableWrap">
          <div className="rdCard__title rdCard__title--bar">
            <span className="rdCard__titleBar">All Corrective Categories</span>
            {topCategories.length > 0 && (
              <span className="rdCard__note">Click to see findings</span>
            )}
          </div>
          <DashboardTable
            columns={categoryColumns}
            rows={topCategories}
            getRowId={(r) => r.category}
            onRowClick={(params) => drillByCategory(params.row.category)}
            loading={tableLoading}
          />
        </div>
      </div>

      <div className="rdGrid rdGrid--2">
        <div>
          <div className="rdSectionLabel rdSectionLabel--first">
            Corrective Sub-Categories
          </div>
          <div className="rdCard rdCard--flush">
            <div className="rdCard__hint">
              Fine-grained Sub-Category level (OSHA subparts, PPE,
              Housekeeping, etc.) — a different level of detail than Category
              above, not a duplicate of it
            </div>
            <div className="rdTableWrap">
              <div className="rdCard__title rdCard__title--bar">
                <span className="rdCard__titleBar">
                  All Corrective Sub-Categories
                </span>
                {topSubCategories.length > 0 && (
                  <span className="rdCard__note">Click to see findings</span>
                )}
              </div>
              <DashboardTable
                columns={subCategoryColumns}
                rows={topSubCategories}
                getRowId={(r) => r.subCategory}
                onRowClick={(params) =>
                  drillBySubCategory(params.row.subCategory)
                }
                loading={tableLoading}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="rdSectionLabel rdSectionLabel--first">
            Recurring Finding Questions
          </div>
          <div className="rdCard rdCard--flush">
            <div className="rdCard__hint">
              Same inspection question flagged corrective multiple times —
              signals a systemic issue
            </div>
            {tableLoading ? (
              <TableLoading />
            ) : recurringQuestions.length > 0 ? (
              <div className="rdTableWrap">
                <table className="rdTable">
                  <thead>
                    <tr>
                      <th>Question</th>
                      <th className="rdTable__cell--right">Times Found</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recurringQuestions.slice(0, 10).map((q) => (
                      <tr key={q.question}>
                        <td
                          className="rdTable__cell--truncate"
                          title={toPlainText(q.question)}
                        >
                          {toPlainText(q.question)}
                        </td>
                        <td className="rdTable__cell--right rdTable__cell--value is-corrective">
                          {q.count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="rdEmpty rdEmpty--center">
                No recurring findings
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="rdSectionLabel">By Asset Type</div>
      <StatCardRow items={assetCards} columns={4} />

      <div className="rdSectionLabel">Findings by Day of Week</div>
      <div className="rdCard rdCard--flush">
        <div className="rdCard__title">
          <span>Item Volume by Day</span>
          <span className="rdCard__note">lower corrective % = better</span>
        </div>
        <div className="rdCard__hint">
          Counts every logged item (Kudos, Corrective, Action, Reprimand) by
          the day it was entered — not a count of inspection visits
        </div>
        {DAYS.map((day) => {
          const all = dowHeatmap.all[day] || 0;
          const corr = dowHeatmap.corrective[day] || 0;
          const corrPct = all > 0 ? Number(((corr / all) * 100).toFixed(1)) : 0;
          const barTone =
            corrPct > 10 ? "corrective" : corrPct > 5 ? "warning" : "accent";
          return (
            <div className="rdMeterRow" key={day}>
              <div className="rdMeterRow__label">{day.slice(0, 3)}</div>
              <div className="rdMeterRow__track">
                <div className="rdBar rdBar--labelled">
                  <div
                    className={`rdBar__fill rdBar__fill--soft is-${barTone}`}
                    style={{ "--rd-fill": `${(all / maxDow) * 100}%` }}
                  />
                  <span className="rdBar__inlineValue">{all}</span>
                </div>
              </div>
              <div
                className={`rdMeterRow__suffix is-${corrPct > 5 ? "danger" : "muted"}`}
              >
                {corrPct}% corr
              </div>
            </div>
          );
        })}
      </div>

      <div className="rdGrid rdGrid--2">
        <div>
          <div className="rdSectionLabel rdSectionLabel--first">
            Assets by Corrective Items
          </div>
          <div className="rdCard rdCard--flush">
            <div className="rdTableWrap">
              <div className="rdCard__title rdCard__title--bar">
                <span className="rdCard__titleBar">Hotspot Assets</span>
                {hotspots.length > 0 && (
                  <span className="rdCard__note">Click to drill</span>
                )}
              </div>
              <DashboardTable
                columns={hotspotColumns}
                rows={hotspots}
                getRowId={(r) => r.category}
                onRowClick={(params) => drillByProject(params.row.category)}
                loading={tableLoading}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="rdSectionLabel rdSectionLabel--first">
            Kudos vs Corrective — Over Time
          </div>
          <div className="rdCard rdCard--flush">
            <div className="rdCard__title">
              <span>Monthly Trend</span>
              <span className="rdCard__note">
                corrective % labeled above each bar
              </span>
            </div>
            {recentMonths.length > 0 ? (
              <>
                <div className="rdColumnChart">
                  {recentMonths.map((m) => {
                    const k = m.kudos || 0;
                    const c = m.corrective || 0;
                    const rate = m.corrRate || 0;
                    return (
                      <div className="rdColumnChart__col" key={m.month}>
                        <div className={`rdColumnChart__rate is-${rateTone(rate)}`}>
                          {rate}%
                        </div>
                        <div className="rdColumnChart__stack">
                          <div
                            className="rdColumnChart__bar is-corrective"
                            style={{ "--rd-height": `${(c / maxMonth) * 76}px` }}
                            title={`${c} corrective`}
                          />
                          <div
                            className="rdColumnChart__bar is-kudos"
                            style={{ "--rd-height": `${(k / maxMonth) * 76}px` }}
                            title={`${k} kudos`}
                          />
                        </div>
                        <div className="rdColumnChart__label">
                          {m.month.slice(5)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="rdLegend">
                  <span className="rdLegend__item">
                    <span className="rdSwatch is-kudos" />
                    Kudos
                  </span>
                  <span className="rdLegend__item">
                    <span className="rdSwatch is-corrective" />
                    Corrective
                  </span>
                </div>
              </>
            ) : (
              <div className="rdEmpty">No trend data</div>
            )}
          </div>
        </div>
      </div>

      <StatCardRow
        items={[
          {
            label: "Total Inspectors",
            value: inspectorStats.length,
            tone: "accent",
          },
          {
            label: "Outliers (2× average)",
            value: outliers.length,
            tone: "danger",
            badge: outliers.length > 0 ? "REVIEW" : null,
          },
          {
            label: "Kudos-Only Inspectors",
            value: kudosOnlyCount,
            tone: "kudos",
            subtitle: "10+ items, 0 corrective",
          },
          {
            label: "Inspector Fleet Average",
            value: `${overallRate}%`,
            tone: "warning",
            subtitle: `Across all asset types · outlier threshold: ${(overallRate * 2).toFixed(1)}%`,
          },
        ]}
        columns={4}
      />

      <div className="rdSectionLabel">
        Inspector Corrective Rate — Outlier Detection
      </div>
      <div className="rdCard">
        <div className="rdCard__legendNote">
          Inspector Fleet Average (all asset types):{" "}
          <strong className="rdValue is-accent">{overallRate}%</strong>{" "}
          &nbsp;·&nbsp; Outlier flag at{" "}
          <strong className="rdValue is-danger">
            {(overallRate * 2).toFixed(1)}%
          </strong>
          {sortedInspectors.length > 0 && (
            <> &nbsp;·&nbsp; Click inspector for drill-down</>
          )}
        </div>
        <DashboardTable
          columns={inspectorColumns}
          rows={sortedInspectors}
          getRowId={(r) => r.inspector}
          onRowClick={(params) => openInspectorDetail(params.row)}
          loading={tableLoading}
        />
      </div>

      {drill && (
        <DrillModal
          open
          onClose={closeDrill}
          title={drill.title}
          columns={reportItemDrillColumns}
          idField="reportLogId"
          fetchPage={(p) => fetchReportItems(apiCtx, { ...drill.filter, ...p })}
          // The filter object's own identity changes every render, so it can't sit
          // in `deps` directly (that would reset to page 0 on every re-render, not
          // just a real filter change) — stringify it the same way DrillModal's own
          // usePaginatedList call keys its depsKey internally.
          deps={[apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to, JSON.stringify(drill.filter)]}
        />
      )}

      {detail?.kind === "inspector" && (
        <RecordModal
          open
          onClose={() => setDetail(null)}
          title={`${detail.payload.inspector} — Inspector Profile`}
          fields={[
            { label: "Total Items", value: detail.payload.totalInspections },
            {
              label: "Corrective Rate",
              value: `${detail.payload.correctiveRate}%`,
            },
            {
              label: "Kudos",
              value: `${detail.payload.kudos} (${detail.payload.kudosRate}%)`,
            },
            { label: "Corrective", value: detail.payload.correctiveItems },
            { label: "Assets Covered", value: detail.payload.projects },
            { label: "Active Months", value: detail.payload.activeMonths },
          ]}
          // HTML renders the outlier alert as a `risk-box` only when the
          // inspector is actually an outlier, and a separate "Recommended
          // Action" block always — split the same way here: the warning
          // slot carries just the alert (and only fires for an outlier),
          // the Recommended Action copy is its own `note` section below.
          warning={
            detail.payload.isOutlier
              ? {
                  tone: "danger",
                  text: `⚠ Outlier Alert — This inspector's corrective rate (${detail.payload.correctiveRate}%) is more than 2× the Inspector Fleet Average across all asset types (${overallRate}%). This may indicate stricter inspection standards, a high-risk asset portfolio, or require coaching review.`,
                }
              : null
          }
          sections={[
            {
              type: "note",
              title: "Recommended Action",
              text: detail.payload.isOutlier
                ? "1. Review the inspector's assigned assets for elevated risk profile. 2. Compare corrective categories to the overall average to identify if specific hazard types are driving the rate. 3. Schedule a calibration session with the inspector to align inspection standards. 4. Determine if the corrective rate reflects genuine site conditions or inconsistent scoring."
                : "This inspector is performing within expected range. Continue monitoring monthly.",
            },
          ]}
        />
      )}
    </div>
  );
};

export default memo(InspectionsSection);
